import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LoginData, Tokens } from '../models/auth';
import { User, UserRights, UserRoles } from '../models/user';
import { TokenService } from './token.service';

@Injectable( {
  providedIn: 'root'
} )
export class AuthService {
  redirectUrl = '';
  currentUser!: User | null;

  currentUser$ = new BehaviorSubject<User | null>( null );
  userRights$ = new BehaviorSubject<UserRights | null>( null );

  constructor(
    private http: HttpClient,
    private tokenService: TokenService,
    private router: Router
  ) { }

  login( loginData: LoginData ): Observable<Tokens> {
    this.tokenService.removeToken();
    this.tokenService.removeRefreshToken();

    return this.http.post<Tokens>( `${ environment.apiUrl }/identity`, loginData ).pipe(
      tap( res => {
        this.tokenService.saveToken( res.token );
        this.tokenService.saveRefreshToken( res.refreshToken );

        this.getCurrentUser().subscribe();

        this.router.navigate( ['/home'] );
      } ) );
  }

  refreshToken( refreshData: Tokens ): Observable<Tokens> {
    this.tokenService.removeToken();
    this.tokenService.removeRefreshToken();

    return this.http.post<Tokens>( `${ environment.apiUrl }/identity/refresh`, refreshData ).pipe(
      tap( res => {
        this.tokenService.saveToken( res.token );
        this.tokenService.saveRefreshToken( res.refreshToken );
      } ) );
  }

  logout( preventNavigation: boolean = false ): void {
    this.tokenService.removeToken();
    this.tokenService.removeRefreshToken();

    this.currentUser$.next( null );
    this.currentUser = null;

    localStorage.removeItem( 'user' );

    if ( !preventNavigation ) {
      this.router.navigate( ['/login'] );
    }
  }

  getCurrentUser(): Observable<User> {
    return this.http.get<User>( `${ environment.apiUrl }/userManagement/users/current` ).pipe(
      tap( ( res: User ) => {
        this.currentUser$.next( res );
        this.currentUser = res;

        localStorage.setItem( 'user', JSON.stringify( res ) );

        this.manageUserRights( res.role );
      } ) );
  }

  getCurrentUserFromLocalStorage(): User {
    const userString = localStorage.getItem( 'user' );
    const user = userString ? JSON.parse( userString ) : null;

    if ( user ) {
      this.currentUser$.next( user );
      this.currentUser = user;

      this.manageUserRights( user.role );
    }

    return user;
  }

  verifyRegistrationToken( userId: string, token: string ): Observable<boolean> {
    // Manually encode the token
    const params = new HttpParams()
      .set( 'userId', userId )
      .set( 'token', encodeURIComponent( token ) );

    return this.http.get<boolean>( `${ environment.apiUrl }/identity/verifyRegistrationToken`, { params } );
  }

  verifyPasswordResetToken( userId: string, token: string ): Observable<boolean> {
    // Manually encode the token
    const params = new HttpParams()
      .set( 'userId', userId )
      .set( 'token', encodeURIComponent( token ) );

    return this.http.get<boolean>( `${ environment.apiUrl }/identity/verifyPasswordResetToken`, { params } );
  }

  register( userId: string, token: string, password: string, passwordConfirmation: string ): Observable<void> {
    // Manually encode the token
    const params = new HttpParams()
      .set( 'userId', userId )
      .set( 'password', password )
      .set( 'passwordConfirmation', passwordConfirmation )
      .set( 'token', encodeURIComponent( token ) );

    return this.http.put<void>( `${ environment.apiUrl }/userManagement/users/${ userId }/confirmEmail`, null, { params } );
  }

  requestResetPassword( username: string ): Observable<void> {
    return this.http.post<void>( `${ environment.apiUrl }/userManagement/users/requestResetPassword`, { username } );
  }

  resetPassword( userId: string, resetPasswordToken: string, password: string, passwordConfirmation: string ): Observable<void> {
    return this.http.put<void>( `${ environment.apiUrl }/userManagement/users/${ userId }/resetPassword`,
      { password, passwordConfirmation, resetPasswordToken: encodeURIComponent( resetPasswordToken ) } );
  }

  manageUserRights( role: UserRoles ) {
    const isAdmin = role === UserRoles.Admin;
    const isXvivoAdmin = role === UserRoles.XvivoAdmin;
    const isXvivoAdminOrAdmin = isXvivoAdmin || isAdmin;
    const isUserOrAdmin = isAdmin || role === UserRoles.User;
    const isUserAdminOrViewerPlus = isUserOrAdmin || role === UserRoles.ViewerPlus;
    const isUserAdminOrViewerPlusOrXvivoSupport = isUserAdminOrViewerPlus || role === UserRoles.XvivoSupport;

    this.userRights$.next( {
      updateDeviceName: isUserOrAdmin,

      linkUnlinkHa: isUserOrAdmin,

      downloadVideo: isUserAdminOrViewerPlus,

      exportData: isUserAdminOrViewerPlusOrXvivoSupport,
      requestRunDelete: isUserOrAdmin,
      toggleSupportAccess: isUserOrAdmin,
      manageRunViewers: isUserOrAdmin, // check with Didi

      manage: isXvivoAdminOrAdmin,

      manageCompanies: isXvivoAdmin,
      managePartners: isAdmin,
      manageUsers: isXvivoAdminOrAdmin,
      manageViewers: isAdmin,
      manageDeleteRequests: isXvivoAdminOrAdmin,
      manageContent: isXvivoAdmin
    } );
  }

  getUserRights() {
    return this.userRights$;
  }

  getUserCompanyId() {
    return this.currentUser ? this.currentUser.companyId : null;
  }
}
